import React, { lazy, Suspense } from 'react';
import { Loading, Layout } from '@folio/stripes/components';

// Lazy load display components for better performance
const GenericTableView = lazy(() => import('../views/table/GenericTableView'));
const GenericPivotTable = lazy(() => import('../views/table/GenericPivotTable'));
const GenericGroupedView = lazy(() => import('../views/table/GenericGroupedView'));
const GenericBarChart = lazy(() => import('../views/charts/GenericBarChart'));
const GenericPieChart = lazy(() => import('../views/charts/GenericPieChart'));
const GenericLineChart = lazy(() => import('../views/charts/GenericLineChart'));
const GenericAreaChart = lazy(() => import('../views/charts/GenericAreaChart'));
const GenericNumericalView = lazy(() => import('../views/numerical/GenericNumericalView'));

// Display method registry with lazy loading
const displayMethods = {
  table: GenericTableView,
  pivotTable: GenericPivotTable,
  groupedView: GenericGroupedView,
  barChart: GenericBarChart,
  pieChart: GenericPieChart,
  lineChart: GenericLineChart,
  areaChart: GenericAreaChart,
  numLabel: GenericNumericalView,
};

// Loading fallback component
const LoadingFallback = () => (
  <Layout className="centered full" style={{ maxWidth: '15rem', height: '8rem' }}>
    &nbsp;
    <Loading size="xlarge" />
  </Layout>
);

// Registry functions
export const getDisplayComponent = (displayMethod) => {
  if (!displayMethod) return displayMethods.table;
  // normalize: 'Table' -> 'table', 'BarChart' -> 'barChart'
  const normalized = displayMethod.charAt(0).toLowerCase() + displayMethod.slice(1);
  return displayMethods[normalized] || displayMethods[displayMethod] || displayMethods.table;
};

// Error boundary for display components
class DisplayErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      console.error('[DisplayErrorBoundary] Error in display component:', this.state.error);
      return (
        <div style={{ padding: '1rem', color: 'red', fontSize: '12px' }}>
          Error: {this.state.error?.message}
        </div>
      );
    }
    return this.props.children;
  }
}

export const renderDisplayComponent = (displayMethod, props) => {
  const Component = getDisplayComponent(displayMethod);

  if (!Component) {
    console.warn(`Display method "${displayMethod}" not found in registry`);
    return null;
  }

  return (
    <DisplayErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <Component {...props} />
      </Suspense>
    </DisplayErrorBoundary>
  );
};

export const getAvailableDisplayMethods = () => {
  return Object.keys(displayMethods);
};

export const registerDisplayMethod = (name, component) => {
  displayMethods[name] = component;
};

export default {
  getDisplayComponent,
  renderDisplayComponent,
  getAvailableDisplayMethods,
  registerDisplayMethod,
};